import auth0 from 'auth0-js'
import * as Sentry from '@sentry/browser'
import config from '../config'
import { useAppStore, type User } from '../stores/app'

interface AuthResult {
  idToken?: string
  accessToken?: string
  idTokenPayload?: any
  expiresIn?: number
  memberId?: string
}

interface Profile {
  name?: string
  expires?: Date
  exp?: number
  [key: string]: any
}

const webAuth = new auth0.WebAuth({
  domain: config.domain,
  redirectUri: `${window.location.origin}/callback`,
  clientID: import.meta.env.VITE_AUTH_CLIENT_ID,
  responseType: 'token id_token',
  scope: 'openid profile email',
  audience: 'https://api.mallshoplink.com/',
})

const loginStatusKey = 'loggedIn'
const loginNameKey = 'loggedInName'
const dataFromTokenKey = 'dataFromToken'
// Persistence key for the "ASP.NET cookie may still be set" signal. Survives
// reloads so that, if a logout's /auth/logout failed silently and the user
// reloads before retrying, the next auth transition still knows to attempt
// fail-closed cookie cleanup before publishing a new identity.
const alternateCookiePresentKey = 'alternateCookiePresent'
const tokenLoginPath = '/auth/login'
const tokenLogoutPath = '/auth/logout'

// Safe localStorage helpers
function getStoredProfile(): Profile | null {
  try {
    const storedProfile = localStorage.getItem(loginNameKey)
    if (!storedProfile) return null
    const parsed = JSON.parse(storedProfile) as any
    // Convert expires string back to Date object
    if (parsed.expires) {
      parsed.expires = new Date(parsed.expires)
    }
    return parsed as Profile
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

function setStoredProfile(profile: Profile): void {
  try {
    localStorage.setItem(loginNameKey, JSON.stringify(profile))
  } catch (error) {
    Sentry.captureException(error)
  }
}

class AuthService {
  idToken: string | null = null
  accessToken: string | null = null
  profile: Profile | null = null
  tokenExpiry: Date | null = null
  accessTokenExpiry: Date | null = null
  isAlternateToken = false
  private authStateVersion = 0
  private activeAuthRequestController: AbortController | null = null
  // Tracks whether the magic-link ASP.NET auth cookie may still be set on the
  // browser. This intentionally survives localStorage being cleared by a new
  // auth transition so that a superseding flow (e.g. an Auth0 callback that
  // races with a magic-link replacement) still knows to fail-closed cookie
  // cleanup before publishing the new identity. Persisted under
  // `alternateCookiePresentKey` so it also survives full page reloads after a
  // failed-and-swallowed logout.
  private alternateCookieMaybePresent: boolean =
    typeof localStorage !== 'undefined' &&
    (localStorage.getItem(alternateCookiePresentKey) === '1' ||
      localStorage.getItem(loginStatusKey) === 'alternate')

  private markAlternateCookieMaybePresent(): void {
    this.alternateCookieMaybePresent = true
    try {
      localStorage.setItem(alternateCookiePresentKey, '1')
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  private markAlternateCookieCleared(): void {
    this.alternateCookieMaybePresent = false
    try {
      localStorage.removeItem(alternateCookiePresentKey)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  private abortActiveAuthRequest(): void {
    this.activeAuthRequestController?.abort()
    this.activeAuthRequestController = null
  }

  private createAuthRequestController(): AbortController {
    this.abortActiveAuthRequest()
    this.activeAuthRequestController = new AbortController()
    return this.activeAuthRequestController
  }

  private releaseAuthRequestController(controller: AbortController): void {
    if (this.activeAuthRequestController === controller) {
      this.activeAuthRequestController = null
    }
  }

  private isAbortError(error: unknown): boolean {
    return (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof Error && error.name === 'AbortError')
    )
  }

  private createStaleAuthenticationError(): Error {
    const error = new Error('Stale authentication result ignored')
    error.name = 'StaleAuthenticationError'
    return error
  }

  isExpectedSupersededAuthError(error: unknown): boolean {
    return (
      this.isAbortError(error) ||
      (error instanceof Error && error.name === 'StaleAuthenticationError')
    )
  }

  private clearSessionState(): number {
    this.abortActiveAuthRequest()
    this.idToken = null
    this.accessToken = null
    this.tokenExpiry = null
    this.profile = null
    this.accessTokenExpiry = null
    this.isAlternateToken = false
    this.authStateVersion += 1
    return this.authStateVersion
  }

  login(customState?: any): void {
    webAuth.authorize({
      appState: customState,
    })
  }

  logout(): void {
    const wasAlternateLogin = localStorage.getItem(loginStatusKey) === 'alternate'

    localStorage.removeItem(loginStatusKey)

    this.clearSessionState()

    // Reset Pinia store
    const store = useAppStore()
    store.resetState()

    if (wasAlternateLogin) {
      // Track this /auth/logout with the active auth controller. If a new
      // login transition starts before this returns, the controller is
      // aborted and a stale cookie clear cannot land after the new cookie.
      const controller = this.createAuthRequestController()
      void this.logoutAlternate({ signal: controller.signal })
        .catch((error) => {
          if (!this.isExpectedSupersededAuthError(error)) {
            Sentry.captureException(error)
          }
        })
        .finally(() => {
          this.releaseAuthRequestController(controller)
        })
    } else {
      webAuth.logout({
        returnTo: `${window.location.origin}`,
      })
    }
    localStorage.removeItem(loginNameKey)
  }

  logoutAlternate(options: { signal?: AbortSignal } = {}): Promise<Response> {
    return fetch(
      import.meta.env.VITE_REVENUE_API_URL + tokenLogoutPath,
      {
        method: 'POST',
        credentials: 'include',
        signal: options.signal,
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      // ASP.NET cookie was confirmed cleared by the server.
      this.markAlternateCookieCleared()
      return response
    })
  }

  handleAuthentication(alternateToken?: string, memberId?: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      if (alternateToken && memberId) {
        // Use the authoritative cookie-presence signal rather than
        // `isAlternateLogin()`, which (a) returns false for expired alternate
        // sessions even though their ASP.NET cookie may still be set, and
        // (b) has the side effect of clearing localStorage on expiry.
        // ADR-006 §7 requires that expired alternate sessions still attempt
        // ASP.NET cookie cleanup.
        const wasAlternateLogin = this.alternateCookieMaybePresent
        const authTransitionVersion = this.clearSessionState()
        localStorage.removeItem(loginStatusKey)
        localStorage.removeItem(loginNameKey)
        const store = useAppStore()
        store.resetState()
        const authRequestController = this.createAuthRequestController()

        const clearPreviousAlternateSession = wasAlternateLogin
          ? this.logoutAlternate({ signal: authRequestController.signal }).catch((error) => {
              if (authTransitionVersion !== this.authStateVersion || this.isAbortError(error)) {
                throw error
              }
              Sentry.captureException(error)
              throw error
            })
          : Promise.resolve()

        clearPreviousAlternateSession
          .then(() => {
            if (authTransitionVersion !== this.authStateVersion) {
              throw this.createStaleAuthenticationError()
            }
            return fetch(
              import.meta.env.VITE_REVENUE_API_URL + tokenLoginPath,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token: alternateToken, identifier: memberId }),
                signal: authRequestController.signal,
              }
            )
          })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            if (authTransitionVersion !== this.authStateVersion) {
              throw this.createStaleAuthenticationError()
            }
            this.localLogin({ memberId })
            sessionStorage.setItem(dataFromTokenKey, alternateToken.substr(0, 6))
            resolve({ memberId })
          })
          .catch((err: any) => {
            if (!this.isExpectedSupersededAuthError(err)) {
              Sentry.captureException(err)
            }
            if (authTransitionVersion === this.authStateVersion) {
              localStorage.removeItem(loginStatusKey)
              localStorage.removeItem(loginNameKey)
              this.clearSessionState()
              store.resetState()
            }
            reject(err)
          })
          .finally(() => {
            this.releaseAuthRequestController(authRequestController)
          })
      } else {
        let authCallbackVersion = this.authStateVersion
        webAuth.parseHash((err: auth0.Auth0ParseHashError | null, authResult: auth0.Auth0DecodedHash | null) => {
          if (err) {
            if (authCallbackVersion !== this.authStateVersion) {
              reject(this.createStaleAuthenticationError())
              return
            }
            Sentry.captureException(err)
            reject(err)
          } else if (authResult && authResult.idToken) {
            const mappedResult: AuthResult = {
              idToken: authResult.idToken,
              accessToken: authResult.accessToken || undefined,
              idTokenPayload: authResult.idTokenPayload,
              expiresIn: authResult.expiresIn || undefined,
            }

            const publishAuth0Result = (): void => {
              if (authCallbackVersion !== this.authStateVersion) {
                reject(this.createStaleAuthenticationError())
                return
              }
              this.abortActiveAuthRequest()
              this.localLogin(mappedResult)
              resolve(mappedResult)
            }

            if (authCallbackVersion !== this.authStateVersion) {
              reject(this.createStaleAuthenticationError())
              return
            }

            // Use the AuthService-level "cookie may still be set" flag rather
            // than localStorage. A racing magic-link replacement may have
            // already cleared loginStatusKey before its own /auth/logout
            // could complete, so localStorage alone would falsely report
            // "no cleanup required" and leave a stale ASP.NET cookie behind.
            if (!this.alternateCookieMaybePresent) {
              publishAuth0Result()
              return
            }

            // Synchronously supersede any concurrent transition (e.g. a
            // magic-link replacement that is mid-cleanup). Bumping the
            // version BEFORE any await/microtask boundary guarantees the
            // superseded transition's outer .catch sees the version
            // mismatch and skips its own state-clobbering branch — so the
            // outcome no longer depends on microtask ordering between
            // chained promise handlers. This bump is also captured by
            // publishAuth0Result via the `let` binding above, so Auth0's
            // own version check still passes.
            authCallbackVersion = ++this.authStateVersion

            const authRequestController = this.createAuthRequestController()
            this.logoutAlternate({ signal: authRequestController.signal }).then(() => {
              publishAuth0Result()
            }).catch(logoutErr => {
              if (authCallbackVersion !== this.authStateVersion || this.isAbortError(logoutErr)) {
                reject(this.createStaleAuthenticationError())
                return
              }
              // Fail-closed per ADR-006: if the prior alternate cookie could
              // not be cleared, do NOT publish Auth0. Reset local state so
              // the user is not left in a mixed-auth condition where the
              // browser presents Auth0 identity but still carries the old
              // ASP.NET cookie on credentialed requests.
              Sentry.captureException(logoutErr)
              localStorage.removeItem(loginStatusKey)
              localStorage.removeItem(loginNameKey)
              this.clearSessionState()
              const store = useAppStore()
              store.resetState()
              reject(logoutErr)
            }).finally(() => {
              this.releaseAuthRequestController(authRequestController)
            })
          } else {
            reject(new Error('No authentication result received'))
          }
        })
      }
    })
  }

  private mapProfileToUser(profile: Profile): User {
    return {
      msToExpire: profile.expires ? profile.expires.getTime() : undefined,
      name: profile.name,
      sub: profile.sub,
      email: profile.email,
      email_verified: profile.email_verified,
      picture: profile.picture,
      nickname: profile.nickname,
    }
  }

  isAuthenticated(): boolean {
    const loginStatus = localStorage.getItem(loginStatusKey)
    if (loginStatus === 'alternate') {
      const storedProfile = getStoredProfile()
      if (storedProfile) {
        this.profile = storedProfile
      }
    }
    if (loginStatus === 'alternate' && this.isAlternateLogin()) {
      const store = useAppStore()
      if (this.profile) {
        store.setAuthenticatedUser(this.mapProfileToUser(this.profile))
      }
      return true
    }
    return loginStatus === 'true'
  }

  isIdTokenValid(): boolean {
    return (
      !!this.idToken && !!this.tokenExpiry && Date.now() < this.tokenExpiry.getTime()
    )
  }

  isAccessTokenValid(): boolean {
    return (
      !!this.accessToken &&
      !!this.accessTokenExpiry &&
      Date.now() < this.accessTokenExpiry.getTime()
    )
  }

  getUser(): Profile | null {
    return this.profile
  }

  getAccessToken(): Promise<string | undefined> {
    return new Promise((resolve) => {
      if (this.isAccessTokenValid()) {
        resolve(this.accessToken!)
      } else if (this.isAlternateLogin() === false) {
        this.renewTokens()
          .then((authResult) => {
            resolve(authResult.accessToken)
          })
          .catch(() => {
            resolve(undefined)
          })
      } else {
        resolve(undefined)
      }
    })
  }

  localLogin(authResult: AuthResult, options: { incrementVersion?: boolean } = {}): void {
    const store = useAppStore()
    const incrementVersion = options.incrementVersion !== false
    
    if (authResult.memberId) {
      if (incrementVersion) {
        this.authStateVersion += 1
      }
      this.profile = {
        name: authResult.memberId,
        expires: new Date(Date.now() + 1000 * 60 * 60),
      }
      localStorage.setItem(loginStatusKey, 'alternate')
      setStoredProfile(this.profile)
      // /auth/login set the ASP.NET cookie; future transitions must clean it.
      this.markAlternateCookieMaybePresent()
      store.setAuthenticatedUser(this.mapProfileToUser(this.profile))
    } else if (authResult.idToken && authResult.idTokenPayload) {
      if (incrementVersion) {
        this.authStateVersion += 1
      }
      this.idToken = authResult.idToken
      this.profile = authResult.idTokenPayload
      this.accessToken = authResult.accessToken || null

      // Convert the expiry time from seconds to milliseconds,
      // required by the Date constructor
      this.tokenExpiry = this.profile?.exp ? new Date(this.profile.exp * 1000) : null

      // Convert expiresIn to milliseconds and add the current time
      // (expiresIn is a relative timestamp, we want an absolute time)
      this.accessTokenExpiry = new Date(Date.now() + authResult.expiresIn! * 1000)
      sessionStorage.removeItem(dataFromTokenKey)
      localStorage.removeItem(loginNameKey)
      localStorage.setItem(loginStatusKey, 'true')
      store.setAuthenticatedUser(this.mapProfileToUser(authResult.idTokenPayload))
    } else {
      throw new Error('Invalid authentication result: missing required data')
    }
  }

  renewTokens(): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const loginStatus = localStorage.getItem(loginStatusKey)
      if (loginStatus !== 'true') {
        this.isAuthenticated()
        reject(new Error('Not logged in'))
      } else {
        const renewalVersion = this.authStateVersion
        webAuth.checkSession({}, (err: auth0.Auth0Error | null, authResult: auth0.Auth0DecodedHash | null) => {
          const currentLoginStatus = localStorage.getItem(loginStatusKey)

          if (currentLoginStatus !== 'true') {
            reject(new Error(`Not logged in. loginStatus is ${currentLoginStatus}`))
          } else if (renewalVersion !== this.authStateVersion) {
            reject(new Error('Stale authentication renewal ignored'))
          } else if (err) {
            Sentry.withScope((scope) => {
              scope.setExtra('authResult', authResult)
              scope.setExtra(loginStatusKey, currentLoginStatus)
              scope.setExtra('renewalVersion', renewalVersion)
              scope.setExtra('authStateVersion', this.authStateVersion)
              Sentry.captureException(err)
            })
            this.logout()
            reject(err)
          } else if (authResult) {
            const mappedResult: AuthResult = {
              idToken: authResult.idToken,
              accessToken: authResult.accessToken || undefined,
              idTokenPayload: authResult.idTokenPayload,
              expiresIn: authResult.expiresIn || undefined,
            }
            this.localLogin(mappedResult, { incrementVersion: false })
            resolve(mappedResult)
          } else {
            reject(new Error('Invalid authentication result'))
          }
        })
      }
    })
  }

  isAlternateLogin(): boolean {
    const loginStatus = localStorage.getItem(loginStatusKey)
    const profileToCheck = this.profile?.expires ? this.profile : getStoredProfile()

    if (
      loginStatus === 'alternate' &&
      profileToCheck?.expires &&
      Date.now() > profileToCheck.expires.getTime()
    ) {
      localStorage.removeItem(loginStatusKey)
      localStorage.removeItem(loginNameKey)
      this.clearSessionState()
      const store = useAppStore()
      store.resetState()
      return false
    }
    return loginStatus === 'alternate'
  }

  async loginExpiration(): Promise<Date | undefined> {
    try {
      const isLoginAlternate = this.isAlternateLogin()
      let msToExpire: Date

      if (isLoginAlternate) {
        const storedProfile = getStoredProfile()
        if (!storedProfile?.expires) {
          return undefined
        }
       
        msToExpire = storedProfile.expires
      } else {
        const accessToken = await this.getAccessToken()
        if (!accessToken) {
          return undefined
        }
        try {
          const tokenParts = accessToken.split('.')
          if (tokenParts.length !== 3) {
            throw new Error('Invalid token format')
          }
          const parsedToken = JSON.parse(atob(tokenParts[1])) as { exp?: number }
          if (!parsedToken.exp) {
            return undefined
          }
          msToExpire = new Date(parsedToken.exp * 1000)
        } catch (tokenError) {
          Sentry.captureException(tokenError)
          return undefined
        }
      }
      return new Date(msToExpire)
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setExtra('isAlternateLogin', this.isAlternateLogin())
        Sentry.captureException(e)
      })
      return undefined
    }
  }
}

const AuthEngine = new AuthService()
export default AuthEngine
